import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BackHeader from '../components/BackHeader';
import NavigationBar from '../components/NavigationBar';
import { useAddress } from '../contexts/AddressContext';
import { getCurrentLanguage } from '../config/languageConfig';
import { useTranslation } from '../hooks/useTranslation';
import apiClient from '../api/config';
import { savePromotionUserId } from '../utils/promotionUserIdUtils';

// 프로모션 사용자 등록 요청 타입
interface PromotionUserRegisterRequest {
  promotionCode: string;
  addressId: string;
  email: string;
  lang: string;
}

// 프로모션 사용자 등록 응답 타입
interface PromotionUserRegisterResponse {
  id: string;
  promotionId: string;
  promotionCode: string;
  stayId: string;
  addressId: string;
  email: string;
  lang: string;
  type: string;
  status: string;
  appliedAt: string;
  usedAt: string | null;
  discountAmount: number | null;
  createdDate: string | null;
  updatedDate: string | null;
}

const PromotionCodePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { address } = useAddress();
  const { t } = useTranslation();
  
  // URL state에서 프로모션 정보 가져오기
  const promotionData = location.state?.promotionData;
  
  // 할인 텍스트를 언어별로 변환하는 함수
  const getLocalizedDiscountText = (discountText: string): string => {
    const currentLang = getCurrentLanguage();
    
    if (discountText === 'Free') {
      return discountText; // Free는 그대로 사용 (번역에서 처리)
    }
    
    // "50% OFF" 형태에서 숫자 추출
    const match = discountText.match(/(\d+)%/);
    if (match) {
      const percentage = parseInt(match[1]);
      
      switch (currentLang) {
        case 'zh-CN':
          // 중국어: 50% OFF → 5折优惠
          return `${percentage / 10}折优惠`;
        case 'zh-TW':
          // 대만어: 50% OFF → 5折優惠
          return `${percentage / 10}折優惠`;
        case 'ja':
          // 일본어: 50% OFF → 50%オフ
          return `${percentage}%オフ`;
        default:
          // 영어, 한국어: 50% OFF → 50% OFF
          return discountText;
      }
    }
    
    return discountText;
  };
  
  // 폼 상태
  const [email, setEmail] = useState('');
  const [agreeToPromotionalEmails, setAgreeToPromotionalEmails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [countdown, setCountdown] = useState(3);
  
  // 이메일 유효성 검사
  const isEmailValid = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const isFormValid = email.trim() !== '' && isEmailValid(email) && agreeToPromotionalEmails;
  
  // 프로모션 정보가 없으면 홈으로 리다이렉트
  useEffect(() => {
    if (!promotionData) {
      navigate('/', { replace: true });
    }
  }, [promotionData, navigate]);
  
  // 이메일 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid || !promotionData || !address?.addressId) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const currentLang = getCurrentLanguage();
      const requestData: PromotionUserRegisterRequest = {
        promotionCode: promotionData.code,
        addressId: address.addressId,
        email: email.trim(),
        lang: currentLang === 'zh-CN' ? 'zh-cn' : 
              currentLang === 'zh-TW' ? 'zh-tw' : 
              currentLang.toLowerCase()
      };
      
      const response = await apiClient.post<PromotionUserRegisterResponse>(
        '/api/v1/promotions/users/register',
        requestData
      );
      
      if (response.status === 201) {
        // 프로모션 사용자 ID를 localStorage에 저장
        const promotionUserId = response.data.id;
        savePromotionUserId(promotionUserId, 'promotion-code-page');
        
        // 성공 팝업 표시
        setShowSuccessPopup(true);
        setCountdown(3);
        
        // 3초 카운트다운 후 홈으로 이동
        const countdownInterval = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              navigate('/', { replace: true });
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error registering for promotion:', error);
      setSubmitError(t('promotion.page.registrationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // 프로모션 정보가 없으면 렌더링하지 않음
  if (!promotionData) {
    return null;
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <BackHeader title={t('promotion.page.submitButton')} />
      
      <div className="flex-1 px-4 py-4 pb-12">
        {/* 프로모션 정보 섹션 */}
        <div className="bg-white rounded-xl shadow-sm p-3 mb-3">
          <div className="text-center mb-3">
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('promotion.page.title')}
            </h1>
            
            <div className="space-y-2">
              <div className="flex items-center text-left">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-700">
                  {promotionData.discountText === 'Free' 
                    ? (
                      <>
                        {(() => {
                          const text = t('promotion.page.benefit1.free');
                          const currentLang = getCurrentLanguage();
                          
                          if (currentLang === 'zh-CN') {
                            // 首单享免运费
                            const parts = text.split('免运费');
                            return (
                              <>
                                {parts[0]}
                                <span className="text-red-500 font-semibold">免运费</span>
                                {parts[1] || ''}
                              </>
                            );
                          } else if (currentLang === 'zh-TW') {
                            // 首次下單享免運費
                            const parts = text.split('免運費');
                            return (
                              <>
                                {parts[0]}
                                <span className="text-red-500 font-semibold">免運費</span>
                                {parts[1] || ''}
                              </>
                            );
                          } else if (currentLang === 'ja') {
                            // 初回注文限定：配達料無料
                            const parts = text.split('配達料無料');
                            return (
                              <>
                                {parts[0]}
                                <span className="text-red-500 font-semibold">配達料無料</span>
                                {parts[1] || ''}
                              </>
                            );
                          } else {
                            // Free delivery for your first order
                            const parts = text.split('Free delivery');
                            return (
                              <>
                                <span className="text-red-500 font-semibold">Free delivery</span>
                                {parts[1] || ''}
                              </>
                            );
                          }
                        })()}
                      </>
                    )
                    :
                      <>
                        {(() => {
                          const discountText = t('promotion.page.benefit1.discount');
                          const currentLang = getCurrentLanguage();
                          
                          if (currentLang === 'zh-CN') {
                            // 首单享运费5折优惠
                            const localizedDiscount = getLocalizedDiscountText(promotionData.discountText);
                            const fullText = discountText.replace('{discount}', localizedDiscount);
                            const parts = fullText.split(localizedDiscount);
                            return (
                              <>
                                {parts[0]}
                                <span className="text-red-500 font-semibold">{localizedDiscount}</span>
                                {parts[1] || ''}
                              </>
                            );
                          } else if (currentLang === 'zh-TW') {
                            // 首次下單享運費5折優惠
                            const localizedDiscount = getLocalizedDiscountText(promotionData.discountText);
                            const fullText = discountText.replace('{discount}', localizedDiscount);
                            const parts = fullText.split(localizedDiscount);
                            return (
                              <>
                                {parts[0]}
                                <span className="text-red-500 font-semibold">{localizedDiscount}</span>
                                {parts[1] || ''}
                              </>
                            );
                          } else if (currentLang === 'ja') {
                            // 初回注文限定：配達料が50%オフ
                            const localizedDiscount = getLocalizedDiscountText(promotionData.discountText);
                            const fullText = discountText.replace('{discount}', localizedDiscount);
                            const parts = fullText.split(localizedDiscount);
                            return (
                              <>
                                {parts[0]}
                                <span className="text-red-500 font-semibold">{localizedDiscount}</span>
                                {parts[1] || ''}
                              </>
                            );
                          } else {
                            // 50% OFF your delivery fee for your first order
                            const localizedDiscount = getLocalizedDiscountText(promotionData.discountText);
                            const fullText = discountText.replace('{discount}', localizedDiscount);
                            const parts = fullText.split(localizedDiscount);
                            return (
                              <>
                                <span className="text-red-500 font-semibold">{localizedDiscount}</span>
                                {parts[1] || ''}
                              </>
                            );
                          }
                        })()}
                      </>
                  }
                </span>
              </div>
              
              <div className="flex items-center text-left">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-700">{t('promotion.page.benefit2')}</span>
              </div>
              
              <div className="flex items-center text-left">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-700">{t('promotion.page.benefit3')}</span>
              </div>
              
              <div className="flex items-center text-left">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-gray-700">
                  {(() => {
                    const currentLang = getCurrentLanguage();
                    
                    if (currentLang === 'zh-CN') {
                      // 简单快捷：选好下单，安心等餐！
                      return (
                        <>
                          简单快捷：<span className="text-red-500 font-semibold">选好下单，安心等餐！</span>
                        </>
                      );
                    } else if (currentLang === 'zh-TW') {
                      // 簡單又快速：選餐、付款、坐等送達！
                      return (
                        <>
                          簡單又快速：<span className="text-red-500 font-semibold">選餐、付款、坐等送達！</span>
                        </>
                      );
                    } else if (currentLang === 'ja') {
                      // 選んで、払って、あとは待つだけ！簡単配達
                      return (
                        <>
                          <span className="text-red-500 font-semibold">選んで、払って、あとは待つだけ！</span>簡単配達
                        </>
                      );
                    } else {
                      // Fast, simple delivery — Just pick, pay, and relax!
                      return (
                        <>
                          Fast, simple delivery — <span className="text-red-500 font-semibold">Just pick, pay, and relax!</span>
                        </>
                      );
                    }
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* 이메일 입력 폼 */}
        <div className="bg-white rounded-xl shadow-sm p-3">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="email" className="flex items-center text-gray-700 font-medium mb-2">
                <svg className="w-5 h-5 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('promotion.page.emailLabel')}
              </label>
              
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  email && !isEmailValid(email)
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200'
                }`}
                placeholder={t('promotion.page.emailPlaceholder')}
                required
              />
              
              {email && !isEmailValid(email) && (
                <p className="text-red-500 text-sm mt-1">
                  {t('promotion.page.validEmail')}
                </p>
              )}
            </div>
            
            <div className="mb-6">
              <p className="text-[10.5px] text-left text-gray-600 mb-3">
                {t('promotion.page.emailNote')}
              </p>
              
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={agreeToPromotionalEmails}
                  onChange={(e) => setAgreeToPromotionalEmails(e.target.checked)}
                  className="mt-1 w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-600">
                  {t('promotion.page.agreeEmails')}
                </span>
              </label>
            </div>
            
            {submitError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{submitError}</p>
              </div>
            )}
            
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors ${
                isFormValid && !isSubmitting
                  ? 'bg-red-500 hover:bg-red-600 active:bg-red-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? t('promotion.page.submitting') : t('promotion.page.submitButton')}
            </button>
          </form>
        </div>
      </div>
      
      <div className="mt-20">
        <NavigationBar />
      </div>
      
      {/* 성공 팝업 */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center">
            {/* 이메일 아이콘 */}
            <div className="mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                </svg>
              </div>
            </div>
            
            {/* 메시지 */}
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {t('promotion.success.title')}
            </h2>
            <p className="text-gray-600 mb-1">
              {t('promotion.success.description1')}
            </p>
            <p className="text-gray-600 mb-4">
              {t('promotion.success.description2')}
            </p>
            
            {/* 카운트다운 */}
            <p className="text-sm text-gray-500">
              {t('promotion.success.countdown').replace('{seconds}', countdown.toString())}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionCodePage;
